import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { X } from "lucide-react";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useConnectStore } from "@/dentlab/src/store/Connect";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { filterActiveClients, sortClients } from "@/lib/utils/client-utils";
import { ClientSidebarItem } from "./ClientSidebarItem";
import { VendorSidebarItem } from "./VendorSidebarItem";
import { useUploadGuard } from "@/dentlab/src/hooks/useUploadGuard";
import { hasConfigValue } from "../DlSideNavigation";
import { ClientEntityType } from "@/lib/supabase/supabaseTypes";

interface SidebarExtensionPanelProps {
	type: "clients" | "vendors";
	onClose: () => void;
	onItemClick: () => void;
}

export const SidebarExtensionPanel: React.FC<SidebarExtensionPanelProps> = ({
	type,
	onClose,
	onItemClick,
}) => {
	return (
		<div
			className="fixed top-0 h-screen w-56 bg-stone-100 z-50 flex flex-col"
			style={{
				left: "var(--AppBarWidth-EXPANDED)",
				boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
			}}
			data-testid="sidebar-extension-panel"
			aria-label={
				type === "clients"
					? "Alle Kunden anzeigen"
					: "Alle Labore anzeigen"
			}
		>
			<div className="h-12 flex items-center justify-between px-3 flex-shrink-0 border-b border-stone-200">
				<span className="text-sm font-semibold text-gray-700">
					{type === "clients" ? "Alle Kunden" : "Alle Fremdlabore"}
				</span>
				<button
					onClick={onClose}
					className="p-1 rounded hover:bg-stone-200 transition-colors text-gray-500 hover:text-gray-700"
					aria-label="Panel schliessen"
					data-testid="extension-panel-close"
				>
					<X size={16} />
				</button>
			</div>
			<div className="flex-1 overflow-y-auto px-1 py-1">
				{type === "clients" ? (
					<ClientsExtensionList onItemClick={onItemClick} />
				) : (
					<VendorsExtensionList onItemClick={onItemClick} />
				)}
			</div>
		</div>
	);
};

const ClientsExtensionList: React.FC<{
	onItemClick: () => void;
}> = ({ onItemClick }) => {
	const { clientId } = useParams();
	const config = useCentralStore((state) => state.organization?.config);
	const showCode = hasConfigValue(config, "sidebar_type", "codes");

	const {
		clientsLookup,
		connectedOrgIdToClientId,
		sortClientsByCode,
		myOrgId,
	} = useCentralStore((state) => {
		const cfg = state.organization?.config as any;
		return {
			clientsLookup: state.clientsLookup,
			connectedOrgIdToClientId: state.connectedOrgIdToClientId,
			sortClientsByCode: cfg?.sort_clients_by_code ?? false,
			myOrgId: state.organization?.id ?? "",
		};
	});

	const unreadCounts = useRealtimeStore((state) =>
		state.getUnreadCountsByClientId(connectedOrgIdToClientId, myOrgId)
	);

	const clients = useMemo(() => {
		const active = filterActiveClients(Object.values(clientsLookup));
		return sortClients(active, sortClientsByCode);
	}, [clientsLookup, sortClientsByCode]);

	return (
		<div className="flex flex-col gap-0.5">
			{clients.map(
				(client: ClientEntityType & { image_src?: string }) => (
					<ClientSidebarItem
						key={client.id}
						client={client}
						isSelected={clientId === client.id}
						expanded={true}
						showCode={showCode}
						onClientInteraction={onItemClick}
						unreadCount={
							client.id ? unreadCounts[client.id] || 0 : 0
						}
					/>
				)
			)}
		</div>
	);
};

const VendorsExtensionList: React.FC<{
	onItemClick: () => void;
}> = ({ onItemClick }) => {
	const checkNavigationAllowed = useUploadGuard();
	const myOrgId = useCentralStore((state) => state.organization?.id ?? "");

	const { connectedVendors, brandsByOrgId } = useConnectStore((state) => ({
		connectedVendors: state.connectedVendors,
		brandsByOrgId: state.brandsByOrgId,
	}));

	const unreadCounts = useRealtimeStore((state) =>
		state.getUnreadCountsByVendorOrgId(myOrgId)
	);

	const vendors = useMemo(() => {
		return connectedVendors
			.map((orgId) => ({
				orgId,
				name: brandsByOrgId[orgId]?.name ?? "Unbekannt",
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [connectedVendors, brandsByOrgId]);

	return (
		<div className="flex flex-col gap-0.5">
			{vendors.map((vendor) => (
				<VendorSidebarItem
					key={vendor.orgId}
					vendorOrgId={vendor.orgId}
					vendorName={vendor.name}
					expanded={true}
					onVendorInteraction={onItemClick}
					onBeforeNavigate={checkNavigationAllowed}
					unreadCount={unreadCounts[vendor.orgId] || 0}
				/>
			))}
		</div>
	);
};
